/**
 * AIDecisionsConfig - Configuration des décisions IA pour l'orchestrateur
 * Permet de choisir quel LLM gère chaque type de décision
 */

import React, { useState, useEffect } from 'react';
import {
  Brain,
  Settings,
  ChevronDown,
  Check,
  RotateCcw,
  Loader2,
  AlertCircle,
  Cpu,
  Zap,
  Shield,
  FileText,
  Target,
  Wrench,
  TrendingUp,
  Activity
} from 'lucide-react';
import { apiClient } from '../api/client';

// Icônes pour chaque type de décision
const DECISION_ICONS = {
  'vulnerability_analysis': Shield,
  'exploit_selection': Target,
  'attack_strategy': TrendingUp,
  'risk_assessment': AlertCircle,
  'post_exploitation': Zap,
  'report_generation': FileText,
  'remediation_advice': Wrench,
  'tool_selection': Settings
};

const AIDecisionsConfig = () => {
  const [decisions, setDecisions] = useState([]);
  const [availableLlms, setAvailableLlms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Charger la configuration au montage
  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/orchestrator/ai-decisions/config');
      setDecisions(response.data.decisions || []);
      setAvailableLlms(response.data.available_llms || []);
    } catch (err) {
      console.error('Erreur chargement config AI decisions:', err);
      setError('Impossible de charger la configuration des décisions IA');
    } finally {
      setLoading(false);
    }
  };

  const updateDecision = async (decisionId, updates) => {
    try {
      setSaving(prev => ({ ...prev, [decisionId]: true }));
      setError(null);

      await apiClient.put(`/orchestrator/ai-decisions/${decisionId}`, updates);

      // Mettre à jour localement
      setDecisions(prev => prev.map(d =>
        d.id === decisionId ? { ...d, ...updates } : d
      ));

      showSuccess(`Configuration de "${decisionId}" mise à jour`);
    } catch (err) {
      console.error('Erreur mise à jour décision:', err);
      setError(`Erreur lors de la mise à jour: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSaving(prev => ({ ...prev, [decisionId]: false }));
    }
  };

  const resetToDefaults = async () => {
    try {
      setLoading(true);
      setError(null);

      await apiClient.post('/orchestrator/ai-decisions/reset');
      await fetchConfig();

      showSuccess('Configuration réinitialisée aux valeurs par défaut');
    } catch (err) {
      console.error('Erreur reset config:', err);
      setError('Erreur lors de la réinitialisation');
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleLlmChange = (decisionId, newLlm) => {
    const decision = decisions.find(d => d.id === decisionId);
    if (decision) {
      updateDecision(decisionId, { llm: newLlm, enabled: decision.enabled });
    }
  };

  const handleToggleEnabled = (decisionId) => {
    const decision = decisions.find(d => d.id === decisionId);
    if (decision) {
      updateDecision(decisionId, { llm: decision.llm, enabled: !decision.enabled });
    }
  };

  const getDecisionIcon = (decisionId) => {
    const IconComponent = DECISION_ICONS[decisionId] || Brain;
    return <IconComponent className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-brand-green" />
          <span className="ml-3 text-gray-400">Chargement de la configuration...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-500/20 rounded-lg">
            <Brain className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Décisions IA</h3>
            <p className="text-sm text-gray-400">Configuration des LLM par type de décision</p>
          </div>
        </div>

        <button
          onClick={resetToDefaults}
          className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 text-sm transition-colors"
          title="Réinitialiser aux valeurs par défaut"
        >
          <RotateCcw className="w-4 h-4" />
          Réinitialiser
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-2 text-red-400">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-3 bg-green-500/20 border border-green-500/50 rounded-lg flex items-center gap-2 text-green-400">
          <Check className="w-5 h-5" />
          {successMessage}
        </div>
      )}

      {/* Liste des décisions */}
      <div className="space-y-3">
        {decisions.sort((a, b) => a.priority - b.priority).map((decision) => (
          <div
            key={decision.id}
            className={`p-4 rounded-lg border transition-all ${
              decision.enabled
                ? 'bg-gray-700/50 border-gray-600 hover:border-purple-500/50'
                : 'bg-gray-800/30 border-gray-700/50 opacity-60'
            }`}
          >
            <div className="flex items-center justify-between gap-4">
              {/* Info décision */}
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className={`p-2 rounded-lg ${decision.enabled ? 'bg-purple-500/20 text-purple-400' : 'bg-gray-700 text-gray-500'}`}>
                  {getDecisionIcon(decision.id)}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-white truncate">{decision.name}</h4>
                    <span className="text-xs px-2 py-0.5 bg-gray-600 rounded text-gray-300">
                      #{decision.priority}
                    </span>
                  </div>
                  <p className="text-sm text-gray-400 truncate">{decision.description}</p>
                </div>
              </div>

              {/* Sélecteur LLM */}
              <div className="flex items-center gap-3">
                <div className="relative">
                  <select
                    value={decision.llm}
                    onChange={(e) => handleLlmChange(decision.id, e.target.value)}
                    disabled={saving[decision.id] || !decision.enabled}
                    className="appearance-none bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 pr-10 text-white text-sm focus:outline-none focus:border-purple-500 disabled:opacity-50 disabled:cursor-not-allowed min-w-[160px]"
                  >
                    {availableLlms.map((llm) => (
                      <option key={llm.id} value={llm.id}>
                        {llm.name}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    {saving[decision.id] ? (
                      <Loader2 className="w-4 h-4 animate-spin text-purple-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                </div>

                {/* Toggle activé/désactivé */}
                <button
                  onClick={() => handleToggleEnabled(decision.id)}
                  disabled={saving[decision.id]}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    decision.enabled ? 'bg-purple-500' : 'bg-gray-600'
                  } ${saving[decision.id] ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span
                    className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                      decision.enabled ? 'left-7' : 'left-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Info LLMs disponibles */}
      <div className="mt-6 pt-4 border-t border-gray-700">
        <h4 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
          <Cpu className="w-4 h-4" />
          LLMs Disponibles
        </h4>
        <div className="flex flex-wrap gap-2">
          {availableLlms.map((llm) => (
            <div
              key={llm.id}
              className="px-3 py-1.5 bg-gray-700/50 rounded-lg text-sm"
              title={llm.description}
            >
              <span className="text-purple-400 font-medium">{llm.name}</span>
              <span className="text-gray-500 ml-2">- {llm.description}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AIDecisionsConfig;
